/**
 * Shared string-layout math used by both visual overlay and pluck detector.
 * SSOT for "where is string N on the stage".
 *
 * Default: uniform spacing (like a physical đàn tranh), lowest at bottom.
 * Optional modes:
 *   - 'cents' spacing places strings proportionally to their absolute
 *     pitch — useful pedagogically so the eye sees the intervals.
 *   - 'low-top' inverts the vertical order so string[0] sits at the top.
 */

export interface LayoutOpts {
  spacing?: 'equal' | 'cents';
  /** When true, string[0] is drawn at top. Default false (real zither). */
  orderLowTop?: boolean;
  /** Required for `spacing === 'cents'`. One MIDI (float OK) per string. */
  pitches?: number[];
}

const EDGE_MARGIN_SLOTS = 1.5;

/** Returns Y (0..1) for every string in draw-order. */
export function computeStringYs(
  stringCount: number,
  opts: LayoutOpts = {},
): number[] {
  if (stringCount <= 0) return [];
  const spacing = opts.spacing ?? 'equal';
  const orderLowTop = opts.orderLowTop ?? false;
  const out: number[] = new Array(stringCount);
  if (spacing === 'cents' && opts.pitches && opts.pitches.length === stringCount) {
    const pitches = opts.pitches;
    const min = Math.min(...pitches);
    const max = Math.max(...pitches);
    const span = Math.max(1e-3, max - min);
    // Keep a 1.5-slot margin on each side so the extreme strings don't
    // touch the stage edge.
    const margin = EDGE_MARGIN_SLOTS / (stringCount + 2 * EDGE_MARGIN_SLOTS);
    const usable = 1 - 2 * margin;
    for (let i = 0; i < stringCount; i++) {
      const norm = (pitches[i]! - min) / span; // 0 (lowest) .. 1 (highest)
      // Low at bottom by default: norm=0 → y near 1-margin.
      const yLowAtBottom = 1 - margin - norm * usable;
      out[i] = orderLowTop ? 1 - yLowAtBottom : yLowAtBottom;
    }
    return out;
  }
  // Equal spacing — slot math: H / (N + 2), 1.5 slots edge margin.
  for (let i = 0; i < stringCount; i++) {
    const visualFromTop = orderLowTop ? i : stringCount - 1 - i;
    out[i] = (EDGE_MARGIN_SLOTS + visualFromTop) / (stringCount + 2);
  }
  return out;
}

/** Legacy helper kept for call-sites that still assume uniform spacing. */
export const stringIndexToY = (index: number, stringCount: number): number => {
  if (stringCount <= 1) return 0.5;
  const visualFromTop = stringCount - 1 - index;
  return (EDGE_MARGIN_SLOTS + visualFromTop) / (stringCount + 2);
};

/**
 * Given a Y crossing from prevY → curY (both 0..1), returns the string
 * index that was crossed in the requested direction, or null.
 */
export const crossedString = (
  prevY: number,
  curY: number,
  direction: 'up' | 'down',
  ys: number[],
): number | null => {
  for (let i = 0; i < ys.length; i++) {
    const y = ys[i]!;
    if (direction === 'down' && prevY < y && curY >= y) return i;
    if (direction === 'up' && prevY > y && curY <= y) return i;
  }
  return null;
};

/**
 * Returns every string crossed by prevY → curY in the given direction,
 * ordered by the time at which each was crossed (nearest to prevY
 * first). Used for glissando, where every string in the sweep fires.
 */
export const crossedStringsOrdered = (
  prevY: number,
  curY: number,
  direction: 'up' | 'down',
  ys: number[],
): number[] => {
  const hits: { i: number; y: number }[] = [];
  for (let i = 0; i < ys.length; i++) {
    const y = ys[i]!;
    if (direction === 'down' && prevY < y && curY >= y) hits.push({ i, y });
    if (direction === 'up' && prevY > y && curY <= y) hits.push({ i, y });
  }
  // Temporal order: downward → ascending y, upward → descending y.
  hits.sort((a, b) => (direction === 'down' ? a.y - b.y : b.y - a.y));
  return hits.map((h) => h.i);
};
